import { Link } from "react-router-dom";
import {
  Cube, Database, ChartLineUp, FileText, ShieldCheck, Stack,
  MapTrifold, Gauge, Cpu, LinkSimple, ArrowRight,
} from "@phosphor-icons/react";
import { Section, SectionHeading, FeatureCard, CalloutCard, BulletList } from "@/components/marketing/MarketingUI";
import CockpitHero from "@/components/marketing/CockpitHero";
import useSEO from "@/lib/useSEO";

/* Cockpit hero content ------------------------------------------------ */
const PRODUCT_COCKPIT = {
  label: "SRES · Product Overview",
  kpis: [
    { label: "MODULES",     to: 4,  suffix: "",  delta: "Coherent flow",   deltaColor: "text-slate-500" },
    { label: "COMMODITIES", to: 12, suffix: "+", delta: "Fe · Au · Cu…",   deltaColor: "text-slate-500" },
    { label: "STANDARDS",   to: 5,  suffix: "",  delta: "JORC · NI · CIM", deltaColor: "text-brand-blue" },
  ],
  variogram: { r2: "0.94", range: "220 m", sill: "0.68", nugget: "0.06" },
  classification: [
    { label: "Measured",  pct: 46 },
    { label: "Indicated", pct: 33 },
    { label: "Inferred",  pct: 21 },
  ],
  status: {
    left: "OK · 3.1 s per 100k blocks",
    right: "4 modules · 1 studio",
  },
};

const MODULES = [
  {
    icon: Database,
    title: "Data & QAQC",
    description:
      "Universal ingest for Collar / Survey / Assay / Geology plus CRM, Blank and Duplicate control samples. Validation catches gaps, overlaps and geometry issues before they contaminate a resource statement.",
    bullets: [
      "CSV, XLSX, SHP, DWG parsers with column alias auto-mapping.",
      "Precision, bias and HARD analytics per batch — with control-chart envelopes.",
      "Domain scripts, saved queries and lithology dictionaries.",
    ],
  },
  {
    icon: Cube,
    title: "Estimation Engines",
    description:
      "Three complementary interpolators — IDW, Nearest Neighbour and Ordinary Kriging — with variography, composite lengths and per-lithology domaining.",
    bullets: [
      "Kriging with fitted spherical / exponential / Gaussian models.",
      "Optional Measured / Indicated / Inferred classification per block.",
      "Density tables mapped by lithology, applied to tonnage automatically.",
    ],
  },
  {
    icon: ChartLineUp,
    title: "Pit & Economics",
    description:
      "Floating-cone pit optimisation with slope constraints, commodity-aware pricing and multi-way economic sensitivity sweeps.",
    bullets: [
      "Cut-off × NPV sweep with live UI slider and downloadable PDF.",
      "Sensitivity across price, mining cost, processing cost and recovery.",
      "Monte-Carlo NPV with triangular distributions and P10 / P50 / P90 outputs.",
    ],
  },
  {
    icon: FileText,
    title: "Competent Person Reporting",
    description:
      "Ship JORC / NI 43-101 / SAMREC-aligned Competent Person Reports directly from the platform — including narrative, charts, signatures and watermarked share links.",
    bullets: [
      "CPR PDF, DOCX exports and per-borehole continuous logs.",
      "Executive, methodology and findings sections drafted by an AI writeup engine.",
      "Public share tokens with expiry, view tracking and audit logs.",
    ],
  },
];

const HIGHLIGHTS = [
  { icon: MapTrifold, title: "Interactive 3D", description: "Plotly WebGL scenes for drillholes, block models and pit shells." },
  { icon: Gauge, title: "AI-suggested defaults", description: "Cut-off, density, search radius proposed from your imported data." },
  { icon: Cpu, title: "Background jobs", description: "Long OK kriging runs queued asynchronously with progress feedback." },
  { icon: Stack, title: "Multi-commodity", description: "Fe, Au, Cu, Ag, Ni, U₃O₈, lithium — with commodity-aware price basis." },
  { icon: ShieldCheck, title: "Multi-tenant auth", description: "Company scoping, admin roles, audit logs and password policy controls." },
  { icon: LinkSimple, title: "Sharable outputs", description: "Watermarked public links for external stakeholders and regulators." },
];

export default function ProductPage() {
  useSEO({
    title: "Product · Four modules, one coherent studio",
    description: "SRES bundles Data & QAQC, Estimation Engines, Pit & Economics, and Competent Person Reporting into a single browser-native studio for defensible resource work.",
    keywords: "SRES product, resource estimation software, block model, kriging, JORC CPR",
    path: "/product",
  });
  return (
    <div data-testid="product-page">
      <CockpitHero
        eyebrow="Product"
        title={<>The single studio<br />your resource team needs.</>}
        description="SRES gathers ingest, QAQC, geostatistics, mine-planning economics and Competent Person reporting into one browser-native platform — with reproducibility, auditability and standards-alignment baked in."
        primaryCta={{ label: "Book a working session", to: "/contact" }}
        secondaryCta={{ label: "See how it works", to: "/how-it-works" }}
        cockpit={PRODUCT_COCKPIT}
        testid="product-hero"
      />

      <Section className="py-20 md:py-28" testid="product-modules">
        <SectionHeading
          eyebrow="Modules"
          title="Four modules. One coherent workflow."
          description="Each SRES module is designed to be used on its own — but they truly shine when your data flows through the entire pipeline."
        />
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {MODULES.map((m, i) => (
            <div
              key={m.title}
              className="p-8 rounded-2xl border border-slate-200 bg-white hover:border-slate-900 transition-colors duration-300"
              data-testid={`product-module-${i}`}
            >
              <div className="flex items-start gap-5">
                <div className="w-11 h-11 rounded-lg border border-slate-200 flex items-center justify-center shrink-0">
                  <m.icon size={20} weight="regular" className="text-slate-700" />
                </div>
                <div>
                  <div className="text-[10.5px] font-mono uppercase tracking-[0.28em] text-slate-400">Module · {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-2 font-heading font-bold text-slate-900 text-[22px] leading-tight tracking-tight">{m.title}</div>
                  <p className="mt-3 text-slate-600 text-[14.5px] leading-[1.7]">{m.description}</p>
                </div>
              </div>
              <div className="mt-7 pt-7 border-t border-slate-100">
                <BulletList items={m.bullets} testid={`product-module-${i}-bullets`} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-28 bg-[#F7F8FB]" testid="product-highlights">
        <SectionHeading
          eyebrow="Under the hood"
          title="Modern engineering, geology-native design."
          description="SRES is built by engineers who ship for scale, working alongside geologists who sign off resource statements every quarter."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {HIGHLIGHTS.map((h, i) => (
            <FeatureCard key={h.title} {...h} testid={`product-highlight-${i}`} />
          ))}
        </div>
      </Section>

      <Section className="py-20 md:py-28" testid="product-comparison">
        <SectionHeading
          eyebrow="Why SRES"
          title="Everything a resource practice needs — nothing it doesn't."
          description="Comparing SRES to the tools we replace in most engagements."
        />
        <div className="mt-10 overflow-x-auto">
          <table className="min-w-full border border-slate-200 rounded-xl overflow-hidden">
            <thead className="bg-brand-ink text-white text-[12px] uppercase tracking-widest">
              <tr>
                <th className="px-6 py-4 text-left font-medium">Capability</th>
                <th className="px-6 py-4 text-left font-medium">SRES</th>
                <th className="px-6 py-4 text-left font-medium">Legacy desktop suites</th>
                <th className="px-6 py-4 text-left font-medium">Spreadsheet workflow</th>
              </tr>
            </thead>
            <tbody className="text-[14px]">
              {[
                ["Browser-native", "Yes", "No", "N/A"],
                ["Multi-tenant collaboration", "Built-in", "Bolted-on", "Ad-hoc"],
                ["Ordinary Kriging + variography", "Yes", "Yes", "No"],
                ["Monte-Carlo NPV", "Yes", "Partial", "Manual"],
                ["Automated CPR PDFs", "Yes", "Manual assembly", "Manual"],
                ["Audit logs & share links", "Yes", "Rare", "None"],
              ].map((row, i) => (
                <tr key={i} className={i % 2 ? "bg-white" : "bg-slate-50"}>
                  <td className="px-6 py-4 font-medium text-slate-900">{row[0]}</td>
                  <td className="px-6 py-4 text-brand-blue font-semibold">{row[1]}</td>
                  <td className="px-6 py-4 text-slate-600">{row[2]}</td>
                  <td className="px-6 py-4 text-slate-600">{row[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section className="py-16 md:py-24" testid="product-cta">
        <CalloutCard
          eyebrow="See SRES on your own data"
          title="Bring a drillhole database. Leave with a first-pass resource statement."
          description="45-minute working session with a senior geologist. No slideware — we build a real model in real time."
          cta="Book a working session"
          to="/contact"
          testid="product-cta-callout"
        />
      </Section>
    </div>
  );
}
